import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation, Trans } from 'react-i18next'
import { adminCompanies } from '../../api/adminClient'
import { useState } from 'react'
import ConfirmModal from '../../components/admin/ConfirmModal'

type PendingItem = { id: string; name: string; tin: string; user_id: string; created_at: string }

export default function AdminRegistrations() {
  const { t } = useTranslation('portal')
  const queryClient = useQueryClient()
  const [rejectReason, setRejectReason] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkNote, setBulkNote] = useState('')
  const [confirmReject, setConfirmReject] = useState<{ id: string; name: string } | null>(null)

  const { data: list, isLoading } = useQuery({
    queryKey: ['admin', 'registrations'],
    queryFn: () => adminCompanies.pending().then((r) => r.data as PendingItem[]),
  })

  const approve = useMutation({
    mutationFn: (id: string) => adminCompanies.approve(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'registrations'] }),
  })

  const bulkApprove = useMutation({
    mutationFn: ({ ids, note }: { ids: string[]; note?: string }) => adminCompanies.bulkApprove(ids, note),
    onSuccess: () => {
      setSelected(new Set())
      setBulkNote('')
      queryClient.invalidateQueries({ queryKey: ['admin', 'registrations'] })
    },
  })

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => adminCompanies.reject(id, reason),
    onSuccess: () => {
      setRejectReason('')
      setConfirmReject(null)
      queryClient.invalidateQueries({ queryKey: ['admin', 'registrations'] })
    },
  })

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectAll = () => {
    if (!list?.length) return
    if (selected.size === list.length) setSelected(new Set())
    else setSelected(new Set(list.map((c) => c.id)))
  }

  const items = list ?? []

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-4">{t('admin.registrations.title')}</h1>
      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <label className="flex items-center gap-2 text-gray-600 text-sm">
            <input
              type="checkbox"
              checked={selected.size === items.length}
              onChange={selectAll}
              className="rounded border-gray-300 bg-white text-emerald-600 focus:ring-emerald-500"
            />
            {t('admin.registrations.selectAll')}
          </label>
          {selected.size > 0 && (
            <>
              <input
                type="text"
                placeholder={t('admin.registrations.noteOptional')}
                value={bulkNote}
                onChange={(e) => setBulkNote(e.target.value)}
                className="rounded-lg bg-white border border-gray-300 px-3 py-2 text-gray-900 text-sm w-48 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <button
                type="button"
                onClick={() => bulkApprove.mutate({ ids: Array.from(selected), note: bulkNote || undefined })}
                disabled={bulkApprove.isPending}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-white text-sm font-medium hover:bg-emerald-500 disabled:opacity-50"
              >
                {t('admin.registrations.approveSelected', { count: selected.size })}
              </button>
            </>
          )}
        </div>
      )}
      <div className="rounded-xl bg-white border border-gray-200 overflow-hidden shadow-sm">
        {isLoading ? (
          <div className="p-8 text-gray-500">{t('admin.common.loading')}</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-gray-500">{t('admin.registrations.noPendingRegistrations')}</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-500 text-sm bg-gray-50">
                <th className="p-3 w-10" />
                <th className="p-3">{t('admin.companies.company')}</th>
                <th className="p-3">{t('admin.companies.tin')}</th>
                <th className="p-3">{t('admin.common.created')}</th>
                <th className="p-3">{t('admin.common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={selected.has(c.id)}
                      onChange={() => toggleSelect(c.id)}
                      className="rounded border-gray-300 bg-white text-emerald-600 focus:ring-emerald-500"
                    />
                  </td>
                  <td className="p-3 text-gray-900 font-medium">{c.name}</td>
                  <td className="p-3 text-gray-600">{c.tin}</td>
                  <td className="p-3 text-gray-500 text-sm">{new Date(c.created_at).toLocaleString()}</td>
                  <td className="p-3 flex gap-2">
                    <button
                      type="button"
                      onClick={() => approve.mutate(c.id)}
                      disabled={approve.isPending}
                      className="px-3 py-1 rounded bg-emerald-600 text-white text-sm hover:bg-emerald-500 disabled:opacity-50"
                    >
                      {t('admin.registrations.approve')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmReject({ id: c.id, name: c.name })}
                      className="px-3 py-1 rounded border border-red-300 text-red-600 text-sm hover:bg-red-50"
                    >
                      {t('admin.registrations.reject')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
      <ConfirmModal
        open={!!confirmReject}
        title={t('admin.registrations.rejectTitle')}
        variant="danger"
        message={
          confirmReject ? (
            <>
              <Trans i18nKey="portal:admin.registrations.rejectMessage" values={{ name: confirmReject.name }} components={{ strong: <strong /> }} />
              <input
                type="text"
                placeholder={t('admin.registrations.reasonOptional')}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 text-sm"
              />
            </>
          ) : null
        }
        confirmLabel={t('admin.registrations.reject')}
        cancelLabel={t('admin.common.cancel')}
        onConfirm={() => confirmReject && reject.mutate({ id: confirmReject.id, reason: rejectReason || t('admin.registrations.rejectedByAdmin') })}
        onCancel={() => { setConfirmReject(null); setRejectReason(''); }}
        loading={reject.isPending}
      />
    </div>
  )
}
